import { useState } from 'react';
import {
  Plus, Pencil, ShieldCheck, Building2, TriangleAlert, CircleCheck, ArrowRight,
  Warehouse, BadgeCheck, Package, Store, ArrowDown, type LucideIcon,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Link } from 'react-router-dom';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable, Pill, type DataTableColumn } from '../../components/TwDataTable';
import {
  PageHeader, Card, Button, IconButton, Modal, Form, useForm, Input, DatePicker, Row, Col, Tag,
  Select, Switch, Alert, Tooltip, message, cn,
} from '../../ui';
import dayjs from 'dayjs';
import { useServerSort } from '../../hooks/useServerSort';

/** One condition standing between the business and switching the engine on (from `/gst/health`). */
interface GstBlocker {
  code: string;
  category: string;
  problem: string;
  action: string;
  link?: string;
  count: number;
  records: { id?: string; label: string; detail?: string; link?: string }[];
}

/**
 * Each blocker area gets its own glyph and tint, because the five areas are five different JOBS on
 * four different screens — the person who owns product HSN codes is not the person who maps a
 * warehouse. A shared grey heading made them one undifferentiated list.
 */
const CATEGORY_META: Record<string, { label: string; icon: LucideIcon; tint: string }> = {
  WAREHOUSES: { label: 'Warehouses', icon: Warehouse, tint: 'bg-sky-500/10 text-sky-600 dark:bg-sky-400/10 dark:text-sky-400' },
  REGISTRATIONS: { label: 'Registrations', icon: BadgeCheck, tint: 'bg-brand-500/10 text-brand-700 dark:bg-brand-400/10 dark:text-brand-400' },
  PRODUCTS: { label: 'Products', icon: Package, tint: 'bg-purple-500/10 text-purple-600 dark:bg-purple-400/10 dark:text-purple-400' },
  CUSTOMERS: { label: 'Dealers', icon: Store, tint: 'bg-warning-500/15 text-warning-700 dark:text-harvest-300' },
  COMPANY_PROFILE: { label: 'Company profile', icon: Building2, tint: 'bg-danger-500/10 text-danger-600 dark:bg-danger-400/10 dark:text-danger-400' },
};
const CATEGORY_FALLBACK = { label: 'Other', icon: TriangleAlert, tint: 'bg-surface-3 text-muted' };

/** The two sections of THIS page a blocker can send you to. */
const REGISTRATIONS_ANCHOR = 'gst-registrations-table';
const WAREHOUSE_MAP_ANCHOR = 'gst-warehouse-map';

/**
 * What each blocker's button should say and where it should go.
 *
 * Several blockers are fixed ON THIS PAGE, and the API says so by returning
 * `link: '/masters/gst-registrations'` — correct, but a link to the route you are already on is a
 * dead click: the reader presses "Fix this" and nothing happens. Those become in-page jumps to the
 * table that actually holds the control (the Verify shield, or the warehouse mapping), which is the
 * thing the sentence was pointing at all along.
 *
 * The labels are the other half: "Fix this" five times over never said WHAT it would do, so the
 * only way to find out was to press it.
 */
const FIX_ACTION: Record<string, { label: string; anchor?: string }> = {
  NO_REGISTRATIONS: { label: 'Add a registration', anchor: REGISTRATIONS_ANCHOR },
  NO_VERIFIED_PRIMARY: { label: 'Verify the head office', anchor: REGISTRATIONS_ANCHOR },
  WAREHOUSE_ON_UNVERIFIED_REGISTRATION: { label: 'Verify these registrations', anchor: REGISTRATIONS_ANCHOR },
  REGISTRATION_STATE_CONTRADICTION: { label: 'Open registrations', anchor: REGISTRATIONS_ANCHOR },
  WAREHOUSE_UNMAPPED: { label: 'Map these warehouses', anchor: WAREHOUSE_MAP_ANCHOR },
};
/** Cross-page destinations, named by where they land rather than by "fix". */
const FIX_LABEL_BY_LINK: Record<string, string> = {
  '/masters/tax': 'Open tax rates',
  '/admin/bank-details': 'Open company profile',
};

/** Scroll a section into view and ring it briefly, so the eye finds it after the jump. */
function jumpToSection(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  el.classList.add('gb-flash');
  window.setTimeout(() => el.classList.remove('gb-flash'), 1700);
}

interface GstRegistration {
  id: string;
  gstin: string;
  stateCode: string;
  stateName?: string | null;
  legalName: string;
  tradeName?: string | null;
  registeredAddress?: string | null;
  isPrimary: boolean;
  isVerified: boolean;
  verifiedByName?: string | null;
  verifiedAt?: string | null;
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  notes?: string | null;
  warehouses: { id: string; code: string; name: string; stateCode: string }[];
}

/**
 * THE GST REGISTRATION MASTER — who the company is, in each state it supplies from.
 *
 * This page exists because the tax engine refuses to guess. Every order is classified intra- or
 * inter-state by comparing the registration that supplies from the fulfilling warehouse against the
 * customer's place of supply, so an unmapped warehouse or an unverified GSTIN stops a document rather
 * than producing a plausible-looking wrong one. The checklist at the top is therefore the important
 * part of the screen: it lists exactly what is standing between the business and a correct tax head.
 */
export function GstRegistrationsPage() {
  const canManage = usePermission('GST.REGISTRATION.ADMIN');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<GstRegistration | null>(null);
  const [mapFor, setMapFor] = useState<{ id: string; code: string; name: string } | null>(null);
  const qc = useQueryClient();

  // Sorting is the backend's ORDER BY (see useServerSort) so every caller of this endpoint sees the
  // same order, and the sort survives a reload or a Back out of a row — it lives in the URL.
  // Two columns are rendered from more than one field: "State" leads with the code, "Effective" with
  // the from-date, and "Supplies from" is a separate lookup so it stays unsortable.
  const { sortState, onSortChange, sort } = useServerSort({
    fieldMap: { state: 'stateCode', effective: 'effectiveFrom' },
  });

  const { data: rows, isLoading } = useQuery({
    queryKey: ['gst-registrations', sort],
    queryFn: () => request<GstRegistration[]>({ url: '/gst/registrations', params: { includeInactive: 'true', ...(sort ? { sort } : {}) } }),
  });
  const { data: health } = useQuery({
    queryKey: ['gst-health'],
    queryFn: () => request<any>({ url: '/gst/health' }),
  });
  // Warehouses that still need a registration — the mapping half of the job lives here, next to the
  // registrations, rather than being hidden one at a time inside each warehouse's edit form.
  const { data: warehouses } = useQuery({
    queryKey: ['warehouses-for-gst'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
  });

  const verify = useConfirmedMutation({ title: "Verify this GST registration?", content: "Once verified it can be mapped to warehouses and used to bill.", okText: "Yes, verify" }, {
    mutationFn: (id: string) => request({ url: `/gst/registrations/${id}/verify`, method: 'POST' }),
    onSuccess: () => {
      message.success('Registration verified — documents may now be supplied under it');
      qc.invalidateQueries({ queryKey: ['gst-registrations'] });
      qc.invalidateQueries({ queryKey: ['gst-health'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? e.response?.data?.message ?? 'Could not verify'),
  });

  const columns: DataTableColumn<GstRegistration>[] = [
    {
      key: 'gstin', title: 'GSTIN', dataIndex: 'gstin', sortable: true, alwaysVisible: true,
      render: (v: string, r) => (
        <div>
          <b className="font-mono">{v}</b>
          {r.isPrimary && <Pill color="gold" className="ml-2">Head office</Pill>}
          {!r.isActive && <Tag tone="neutral" className="ml-2">Inactive</Tag>}
        </div>
      ),
    },
    {
      key: 'state', title: 'State', sortable: true,
      render: (_: any, r) => <span>{r.stateName || r.stateCode} <span className="text-subtle">({r.stateCode})</span></span>,
    },
    { key: 'legalName', title: 'Registered name', dataIndex: 'legalName', sortable: true },
    {
      key: 'isVerified', title: 'Status', align: 'center', width: 150, sortable: true,
      render: (_: any, r) => (r.isVerified
        ? (
          <Tooltip label={r.verifiedByName
            ? `Verified by ${r.verifiedByName}${r.verifiedAt ? ` on ${dayjs(r.verifiedAt).format('DD MMM YYYY')}` : ''}`
            : 'Verified'}>
            <span><Pill color="green">Verified</Pill></span>
          </Tooltip>
        )
        : (
          <Tooltip label="Transcribed automatically — the tax engine will not classify a supply under it until somebody confirms it">
            <span><Pill color="red">Needs confirming</Pill></span>
          </Tooltip>
        )),
    },
    {
      key: 'warehouses', title: 'Supplies from', alwaysVisible: true,
      render: (_: any, r) => (r.warehouses.length
        ? <span>{r.warehouses.map((w) => w.code).join(', ')}</span>
        : <span className="text-subtle">No warehouse mapped</span>),
    },
    {
      key: 'effective', title: 'Effective', width: 190, sortable: true,
      render: (_: any, r) => <span className="text-muted">{r.effectiveFrom}{r.effectiveTo ? ` → ${r.effectiveTo}` : ' → current'}</span>,
    },
    ...(canManage ? [{
      key: 'actions', title: '', alwaysVisible: true, width: 92, align: 'right' as const,
      render: (_: any, r: GstRegistration) => (
        // `justify-end`: the Verify button only exists on an unverified row, so with the group
        // left-aligned the Edit pencil sat at a different x on every other row and the column
        // zig-zagged down the page. Anchored right, Edit is always in the same place and the
        // optional Verify grows leftward from it.
        <div className="flex items-center justify-end gap-1" onClick={(e: any) => e.stopPropagation()}>
          {!r.isVerified && (
            <Tooltip label="Confirm this is genuinely our registration">
              <IconButton diameter={32} aria-label={`Verify ${r.gstin}`}
                className="border border-line text-muted hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                onClick={() => verify.mutate(r.id)}>
                <ShieldCheck size={16} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip label="Edit">
            <IconButton diameter={32} aria-label={`Edit ${r.gstin}`}
              className="border border-line text-muted hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
              onClick={() => { setEditing(r); setOpen(true); }}>
              <Pencil size={16} />
            </IconButton>
          </Tooltip>
        </div>
      ),
    } as DataTableColumn<GstRegistration>] : []),
  ];

  // The SAME blockers the `gst:audit` CLI gate prints — one backend function feeds both, so this
  // screen can no longer show a clean checklist while the gate says BLOCKED.
  const blockers: GstBlocker[] = health?.blockers ?? [];
  const byCategory = blockers.reduce<Record<string, GstBlocker[]>>((acc, b) => {
    (acc[b.category] ??= []).push(b);
    return acc;
  }, {});
  /** Records outstanding across every area — the number that says how big the job actually is. */
  const totalToFix = blockers.reduce((sum, b) => sum + b.count, 0);

  return (
    <div>
      <PageHeader
        title="GST registrations"
        subtitle="Every GSTIN the company holds, and which warehouse supplies under each. This is what decides CGST+SGST vs IGST."
        icon={<Building2 />}
        actions={canManage ? (
          <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditing(null); setOpen(true); }}>
            Add registration
          </Button>
        ) : undefined}
      />

      {/* THE READINESS GATE. Grouped by where the work is, because the fixes live on four different
          screens and a flat list made people hunt. Every item carries the count, the affected records
          and a link straight to the place it is fixed. */}
      {health && (
        health.ok ? (
          <Alert
            className="mb-4"
            type="success"
            title={`GST configuration complete — ${health.verified} verified registration(s), ${health.warehousesMapped}/${health.warehousesTotal} warehouses mapped`}
            description={health.engineEnabled
              ? 'The GST engine is ON: every order is classified from the registration that supplies it.'
              : 'The gate passes. The engine is still OFF (GST_ENGINE_V2_ENABLED) — switching it on is a deliberate deployment step.'}
          />
        ) : (
          /*
           * The gate, as a checklist you can work through rather than a wall of text.
           *
           * This panel is the important half of the screen — it is the only place that says why tax
           * is refusing to classify anything — but it read as five identical grey paragraphs, so the
           * severity, the size of the job and the five different PLACES the work lives were all
           * invisible. Now: an amber band carries the status and the total, and each area is its own
           * tile with an icon, a tone and a count, so the eye can pick the one it owns.
           */
          <section className="mb-4 overflow-hidden rounded-card border border-warning-300 bg-surface shadow-card dark:border-warning-500/40">
            <div className="flex flex-wrap items-start gap-3 border-b border-warning-300 bg-warning-50 px-4 py-3.5 dark:border-warning-500/30 dark:bg-warning-500/10">
              <span className="grid size-9 shrink-0 place-items-center rounded-control bg-warning-500/15 text-warning-600 dark:text-harvest-300">
                <TriangleAlert className="size-5" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="type-section-title text-content">
                  GST engine is off — {blockers.length} thing{blockers.length === 1 ? '' : 's'} to settle
                  {' '}across {Object.keys(byCategory).length} area{Object.keys(byCategory).length === 1 ? '' : 's'}
                </h2>
                <p className="mt-0.5 type-body text-muted">
                  An order it cannot classify would be refused, so the engine stays off until these clear.
                  These are the same conditions <code className="rounded border border-line bg-surface-2 px-1 py-px font-mono type-caption">gst:audit</code> checks.
                </p>
              </div>
              <span className="shrink-0 rounded-pill bg-warning-500/20 px-2.5 py-1 type-label-strong text-warning-700 dark:text-harvest-300">
                {totalToFix} to fix
              </span>
            </div>

            {/*
             * A LIST, not a grid.
             *
             * These five areas hold wildly different amounts — one record against fifteen — so a
             * card grid could only ever be wrong: stretched, the short tiles carry a dead void to
             * match the tall one; hugging, the bottoms come out ragged and the fifth tile leaves a
             * hole in a three-column row. Height-matching is the wrong problem to solve. As full-
             * width rows there is nothing to match: each area is as tall as it needs to be, the
             * glyph + name + count line up down a fixed left rail so the eye can scan the five
             * areas in one pass, and the evidence chips get the whole width instead of stacking
             * three-deep in a narrow column.
             *
             * The rail column is `max-content`, NOT a fixed width. A hard `w-44` truncated
             * "COMPANY PROFILE" to "COMPANY PROFI…" while 800px of the row sat empty beside it —
             * text compressed for no reason, because the width was a guess rather than something
             * derived from the content. As a grid column the rail measures itself against the
             * LONGEST area name and every row still lines up; `display: contents` on the row lets
             * the two cells participate in that shared grid directly.
             */}
            <ul className="grid grid-cols-1 sm:grid-cols-[max-content_1fr]">
              {Object.entries(byCategory).map(([category, items], idx) => {
                const meta = CATEGORY_META[category] ?? CATEGORY_FALLBACK;
                const AreaIcon = meta.icon;
                const n = items.reduce((sum, b) => sum + b.count, 0);
                // The row has no box of its own, so the divider is drawn on both cells — one
                // continuous line across the row at sm+, and above the rail alone when stacked.
                const rule = idx > 0 ? 'border-t border-line' : '';
                return (
                  <li key={category} className="contents">
                    {/* Left rail: what this area is, and how much of it is outstanding. Top-aligned so
                        the label sits against the first line of its problem, not adrift in the middle
                        of a tall row. */}
                    <div className={cn('flex items-center gap-2 px-4 pt-3.5 sm:items-start sm:pb-3.5', rule)}>
                      <span className={cn('grid size-7 shrink-0 place-items-center rounded-control [&>svg]:size-4', meta.tint)}>
                        <AreaIcon />
                      </span>
                      <span className="type-overline text-content">{meta.label}</span>
                      <Tag tone="gold">{n}</Tag>
                    </div>

                    {/* `sm:border-t` only: stacked, the rail's own rule already separates the areas,
                        and a second line here would cut between a heading and its own content. */}
                    <div className={cn('min-w-0 px-4 pb-3.5 pt-2 sm:pl-5 sm:pt-3.5', idx > 0 && 'sm:border-t sm:border-line')}>
                    {items.map((b) => (
                      <div key={b.code} className="mb-3 last:mb-0">
                        <p className="type-body-medium text-content">{b.problem}</p>
                        <p className="mt-1 type-caption text-muted">{b.action}</p>

                        {b.records.length > 0 && (
                          // Chips, not a bullet list: each record is an identifier plus one fact
                          // about it, and at 11px in a column they ran together into prose.
                          <div className="mt-2 flex flex-wrap gap-1">
                            {b.records.slice(0, 6).map((r, i) => {
                              const body = (
                                <>
                                  <span className="truncate type-caption-strong text-content">{r.label}</span>
                                  {r.detail && <span className="truncate type-caption text-subtle">{r.detail}</span>}
                                </>
                              );
                              const chip = 'inline-flex max-w-full items-center gap-1.5 rounded-control border border-line bg-surface px-1.5 py-0.5';
                              return r.link ? (
                                <Link key={`${b.code}-${r.id ?? i}`} to={r.link} className={cn(chip, 'transition-colors hover:border-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20')}>
                                  {body}
                                </Link>
                              ) : (
                                <span key={`${b.code}-${r.id ?? i}`} className={chip}>{body}</span>
                              );
                            })}
                            {b.records.length > 6 && (
                              <span className="inline-flex items-center px-1 type-caption text-subtle">
                                +{b.records.length - 6} more
                              </span>
                            )}
                          </div>
                        )}

                        {(() => {
                          if (!b.link) return null;
                          const act = FIX_ACTION[b.code];
                          const cls = 'mt-2.5 inline-flex items-center gap-1 rounded-control border border-line bg-surface px-2.5 py-1 type-label text-brand-700 transition-colors hover:border-brand-400 hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-900/20';
                          // Fixed on THIS page → jump to the table holding the control, not a
                          // no-op navigation to the route we are already on.
                          if (act?.anchor) {
                            return (
                              <button type="button" className={cls} onClick={() => jumpToSection(act.anchor!)}>
                                {act.label} <ArrowDown size={13} />
                              </button>
                            );
                          }
                          return (
                            <Link to={b.link} className={cls}>
                              {act?.label ?? FIX_LABEL_BY_LINK[b.link] ?? 'Fix this'} <ArrowRight size={13} />
                            </Link>
                          );
                        })()}
                      </div>
                    ))}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )
      )}

      <div id="gst-registrations-table" className="scroll-mt-24 rounded-card">
      <Card bodyClassName="p-0 sm:p-0">
        <div className="p-4">
          <TwDataTable<GstRegistration>
            storageKey="gst-registrations"
            rowKey="id"
            loading={isLoading}
            dataSource={rows ?? []}
            columns={columns}
            sortState={sortState}
            onSortChange={onSortChange}
            autoFit
            emptyText="No GST registration yet — add the GSTIN each state's warehouses supply under."
            pagination={{ pageSize: 25, showSizeChanger: true }}
          />
        </div>
      </Card>
      </div>

      {/* Warehouse → registration mapping. An unmapped warehouse cannot dispatch once the engine is on. */}
      <div id="gst-warehouse-map" className="scroll-mt-24 rounded-card">
      <Card className="mt-4" title="Which registration supplies from each warehouse">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="gb-thead-tint">
              <tr>
                <th className="px-3 py-2.5 text-left type-overline text-muted">Warehouse</th>
                <th className="px-3 py-2.5 text-left type-overline text-muted">State</th>
                <th className="px-3 py-2.5 text-left type-overline text-muted">Supplies under</th>
                {/* No label: the column holds two different verbs (Map / Change), so "Map" named
                    only half of it. Fixed width so the buttons form one straight edge. */}
                {canManage && <th className="w-28 px-3 py-2.5 text-right type-overline text-muted" aria-label="Action" />}
              </tr>
            </thead>
            <tbody>
              {(warehouses ?? []).map((w: any) => {
                const reg = (rows ?? []).find((r) => String(r.id) === String(w.gstRegistrationId));
                return (
                  <tr key={w.id} className="border-t border-line">
                    <td className="px-3 py-2.5 text-content"><b>{w.code}</b> · {w.name}</td>
                    <td className="px-3 py-2.5 text-muted">{w.stateCode}</td>
                    <td className="px-3 py-2.5">
                      {reg
                        ? <span className="font-mono">{reg.gstin} {!reg.isVerified && <Pill color="red" className="ml-1">unverified</Pill>}</span>
                        : (
                          // Pill + consequence, matching the "unverified" treatment on mapped rows —
                          // three identical red sentences down the column read as noise, not warnings.
                          <span className="inline-flex flex-wrap items-center gap-1.5">
                            <Pill color="red">Not mapped</Pill>
                            <span className="type-caption text-muted">orders from here cannot be taxed</span>
                          </span>
                        )}
                    </td>
                    {canManage && (
                      <td className="px-3 py-2.5 text-right">
                        {/* One fixed width for both verbs, so the column has a straight edge instead
                            of alternating between a narrow "Map" and a wider "Change". An unmapped
                            warehouse is blocking work, so that one is the primary action. */}
                        <Button
                          size="sm"
                          variant={reg ? 'default' : 'primary'}
                          className="w-20"
                          onClick={() => setMapFor({ id: String(w.id), code: w.code, name: w.name })}
                        >
                          {reg ? 'Change' : 'Map'}
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      </div>

      <RegistrationFormModal open={open} record={editing} onClose={() => { setOpen(false); setEditing(null); }} />
      <MapWarehouseModal warehouse={mapFor} registrations={rows ?? []} onClose={() => setMapFor(null)} />
    </div>
  );
}

/** Add / edit a registration. The state is NOT asked for — it is read out of the GSTIN. */
function RegistrationFormModal({ open, record, onClose }: { open: boolean; record: GstRegistration | null; onClose: () => void }) {
  const [form] = useForm();
  const qc = useQueryClient();
  const isEdit = !!record;

  const save = useConfirmedMutation({ title: isEdit ? "Save this GST registration?" : "Add this GST registration?", content: "Invoices bill under the registration mapped to the dispatching warehouse.", okText: "Yes, save" }, {
    mutationFn: (v: any) => {
      const data = {
        gstin: String(v.gstin ?? '').trim().toUpperCase(),
        legalName: v.legalName,
        tradeName: v.tradeName || undefined,
        registeredAddress: v.registeredAddress || undefined,
        effectiveFrom: dayjs(v.effectiveFrom).format('YYYY-MM-DD'),
        effectiveTo: v.effectiveTo ? dayjs(v.effectiveTo).format('YYYY-MM-DD') : undefined,
        isPrimary: !!v.isPrimary,
        isActive: v.isActive !== false,
        notes: v.notes || undefined,
      };
      return isEdit
        ? request({ url: `/gst/registrations/${record!.id}`, method: 'PUT', data })
        : request({ url: '/gst/registrations', method: 'POST', data });
    },
    onSuccess: () => {
      message.success(isEdit ? 'Registration updated' : 'Registration added');
      qc.invalidateQueries({ queryKey: ['gst-registrations'] });
      qc.invalidateQueries({ queryKey: ['gst-health'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? e.response?.data?.message ?? 'Could not save'),
  });

  const initialValues = record
    ? {
      ...record,
      effectiveFrom: record.effectiveFrom ? dayjs(record.effectiveFrom) : dayjs(),
      effectiveTo: record.effectiveTo ? dayjs(record.effectiveTo) : undefined,
    }
    : { effectiveFrom: dayjs(), isActive: true, isPrimary: false };

  return (
    <Modal
      open={open} onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? `Edit ${record!.gstin}` : 'Add GST registration'}
      width={620}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>{isEdit ? 'Update' : 'Add'}</Button>
      </>}
    >
      <Form form={form} key={record ? `edit-${record.id}` : 'create'} initialValues={initialValues} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <Form.Item name="gstin" label="GSTIN" rules={[{ required: true }]}
              extra="The state is taken from the first two digits — a registration belongs to exactly one state.">
              <Input placeholder="27ABCDE1234F1Z5" maxLength={15} className="font-mono uppercase" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="legalName" label="Registered legal name" rules={[{ required: true }]}><Input /></Form.Item>
          </Col>
          <Col span={12}><Form.Item name="tradeName" label="Trade name"><Input /></Form.Item></Col>
          <Col span={12}>
            <Form.Item name="isPrimary" label="Head office registration" valuePropName="checked"
              tooltip="Used to price an order provisionally, before its fulfilling warehouse is chosen. Only one registration can hold this.">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={24}><Form.Item name="registeredAddress" label="Registered address"><Input /></Form.Item></Col>
          <Col span={12}>
            <Form.Item name="effectiveFrom" label="Effective from" rules={[{ required: true }]}
              extra="Documents resolve their registration by date, so historical ones keep working.">
              <DatePicker className="w-full" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="effectiveTo" label="Surrendered on"><DatePicker className="w-full" /></Form.Item>
          </Col>
          <Col span={12}><Form.Item name="isActive" label="Active" valuePropName="checked"><Switch /></Form.Item></Col>
          <Col span={24}><Form.Item name="notes" label="Notes"><Input /></Form.Item></Col>
        </Row>
      </Form>
    </Modal>
  );
}

function MapWarehouseModal({
  warehouse, registrations, onClose,
}: { warehouse: { id: string; code: string; name: string } | null; registrations: GstRegistration[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [registrationId, setRegistrationId] = useState<string | undefined>();

  const save = useConfirmedMutation({ title: "Map this warehouse to the selected registration?", content: "Every invoice dispatched from this warehouse will bill under it.", okText: "Yes, map it" }, {
    mutationFn: () => request({ url: '/gst/warehouse-mapping', method: 'POST', data: { warehouseId: warehouse!.id, registrationId: registrationId ?? '' } }),
    onSuccess: () => {
      message.success('Warehouse mapped');
      qc.invalidateQueries({ queryKey: ['warehouses-for-gst'] });
      qc.invalidateQueries({ queryKey: ['gst-registrations'] });
      qc.invalidateQueries({ queryKey: ['gst-health'] });
      setRegistrationId(undefined);
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Could not map the warehouse'),
  });

  return (
    <Modal
      open={!!warehouse} onOpenChange={(o) => { if (!o) { setRegistrationId(undefined); onClose(); } }}
      title={warehouse ? `Which registration supplies from ${warehouse.code}?` : 'Map warehouse'}
      footer={<>
        <Button variant="default" onClick={() => { setRegistrationId(undefined); onClose(); }}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
      </>}
    >
      <Form layout="vertical">
        <Form.Item label="GST registration"
          extra="Goods leaving this warehouse are supplied by this registration — that is what makes a sale intra- or inter-state.">
          <Select
            style={{ width: '100%' }}
            placeholder="Select a registration"
            value={registrationId}
            onChange={(v: any) => setRegistrationId(v)}
            options={registrations.filter((r) => r.isActive).map((r) => ({
              value: String(r.id),
              label: `${r.gstin} · ${r.stateName || r.stateCode}${r.isVerified ? '' : ' (unverified)'}`,
            }))}
          />
        </Form.Item>
      </Form>
      <p className="text-xs text-muted">
        <CircleCheck size={12} className="mr-1 inline" />
        A warehouse in one state may legitimately supply under a registration in another — the mapping is the
        business fact, and this page is where it is recorded rather than inferred.
      </p>
    </Modal>
  );
}
